import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/landing/Logo";

const NAV_LINKS = [
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Solution" },
  { id: "how", label: "How it works" },
  { id: "trust", label: "Trust" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeAndScroll = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 60);
  };

  return (
    <header
      data-testid="nav-bar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-carvny-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="h-16 sm:h-20 flex items-center justify-between gap-6">
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center"
            aria-label="CARVNY home"
          >
            <Logo size="md" testid="nav-logo-mark" />
          </button>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-link-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className="text-[13px] font-semibold text-carvny-ink/65 hover:text-carvny-ink transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTAs (desktop) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="nav-host-cta"
              onClick={() => scrollTo("waitlist")}
              className="px-5 py-2.5 rounded-full text-[13px] font-bold text-carvny-ink hover:text-carvny-red transition-colors"
            >
              Host Your Car
            </button>
            <button
              data-testid="nav-rent-cta"
              onClick={() => scrollTo("waitlist")}
              className="group inline-flex items-center gap-1.5 pl-5 pr-4 py-2.5 rounded-full text-[13px] font-bold bg-carvny-red text-white hover:bg-carvny-red-hover transition-all shadow-[0_10px_24px_-12px_rgba(204,0,0,0.7)]"
            >
              Rent a Car
              <ArrowUpRight size={15} className="transition-transform group-hover:rotate-12" />
            </button>
          </div>

          {/* Mobile menu trigger */}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 -mr-2 text-carvny-ink"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? "max-h-[460px] opacity-100" : "max-h-0 opacity-0"
        } bg-white border-b border-carvny-border`}
      >
        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-mobile-${link.id}`}
              onClick={() => closeAndScroll(link.id)}
              className="text-left py-3 text-base font-semibold text-carvny-ink border-b border-carvny-border last:border-b-0"
            >
              {link.label}
            </button>
          ))}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              data-testid="nav-mobile-host"
              onClick={() => closeAndScroll("waitlist")}
              className="px-4 py-3 rounded-full text-sm font-bold border border-carvny-ink text-carvny-ink"
            >
              Host Your Car
            </button>
            <button
              data-testid="nav-mobile-rent"
              onClick={() => closeAndScroll("waitlist")}
              className="px-4 py-3 rounded-full text-sm font-bold bg-carvny-red text-white"
            >
              Rent a Car
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
